import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  ArrowRight,
  Upload, 
  FileText, 
  User, 
  Mail, 
  Phone, 
  MapPin,
  Calendar,
  CheckCircle,
  AlertCircle,
  Send,
  Briefcase,
  GraduationCap,
  Award,
  DollarSign,
  Clock
} from 'lucide-react'

const JobApplication: React.FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [selectedPosition, setSelectedPosition] = useState('')

  const positions = [
    {
      id: 'blockchain-dev',
      title: '区块链开发工程师',
      department: '技术部',
      location: '北京/上海/深圳',
      type: '全职',
      experience: '3-5年',
      salary: '25-40K',
      description: '负责区块链底层技术开发，智能合约编写，DeFi协议开发',
      requirements: [
        '熟悉Solidity、Rust等区块链开发语言',
        '有DeFi、NFT项目开发经验',
        '了解Layer2、跨链技术',
        '具备良好的代码规范和文档习惯'
      ],
      benefits: ['五险一金', '股权激励', '弹性工作', '技术培训']
    },
    {
      id: 'hardware-engineer',
      title: '硬件工程师',
      department: '硬件部',
      location: '深圳',
      type: '全职',
      experience: '5-8年',
      salary: '30-50K',
      description: '负责CoreTrust手机硬件设计，安全芯片集成，硬件钱包开发',
      requirements: [
        '熟悉ARM架构和移动设备硬件设计',
        '有安全芯片、加密硬件开发经验',
        '了解硬件安全标准和认证流程',
        '具备PCB设计和调试能力'
      ],
      benefits: ['五险一金', '股权激励', '弹性工作', '技术培训']
    },
    {
      id: 'product-manager',
      title: '产品经理',
      department: '产品部',
      location: '北京',
      type: '全职',
      experience: '3-5年',
      salary: '20-35K',
      description: '负责Web3产品规划，用户体验设计，产品迭代管理',
      requirements: [
        '有Web3/区块链产品经验',
        '熟悉产品设计流程和方法论',
        '具备良好的用户洞察能力',
        '有技术背景优先'
      ],
      benefits: ['五险一金', '股权激励', '弹性工作', '产品培训']
    },
    {
      id: 'ui-ux-designer',
      title: 'UI/UX设计师',
      department: '设计部',
      location: '北京/上海',
      type: '全职',
      experience: '2-4年',
      salary: '15-25K',
      description: '负责产品界面设计，用户体验优化，设计系统建设',
      requirements: [
        '熟练使用Figma、Sketch等设计工具',
        '有移动端产品设计经验',
        '了解Web3产品设计特点',
        '具备良好的审美和设计能力'
      ],
      benefits: ['五险一金', '股权激励', '弹性工作', '设计培训']
    },
    {
      id: 'marketing-manager',
      title: '市场推广经理',
      department: '市场部',
      location: '北京',
      type: '全职',
      experience: '3-5年',
      salary: '18-30K',
      description: '负责品牌推广，市场活动策划，合作伙伴关系维护',
      requirements: [
        '有科技产品市场推广经验',
        '了解Web3/区块链行业',
        '具备良好的沟通和协调能力',
        '有媒体资源优先'
      ],
      benefits: ['五险一金', '股权激励', '弹性工作', '市场培训']
    },
    {
      id: 'community-manager',
      title: '社区运营',
      department: '运营部',
      location: '北京/远程',
      type: '全职',
      experience: '1-3年',
      salary: '12-20K',
      description: '负责社区建设，用户运营，内容创作，活动策划',
      requirements: [
        '有社区运营或内容创作经验',
        '了解Web3/区块链文化',
        '具备良好的文字表达能力',
        '有社交媒体运营经验'
      ],
      benefits: ['五险一金', '股权激励', '弹性工作', '运营培训']
    }
  ]

  const companyInfo = {
    name: 'DeepLink Limited',
    founded: '2022年',
    employees: '50-100人',
    industry: 'Web3/区块链',
    description: '专注于Web3硬件设备和生态建设的创新科技公司，致力于打造下一代数字生活体验。',
    culture: [
      '创新驱动：鼓励技术创新和思维突破',
      '团队协作：重视团队合作和知识分享',
      '持续学习：提供丰富的学习和成长机会',
      '开放包容：营造开放包容的工作环境'
    ],
    benefits: [
      { icon: '💰', title: '薪酬福利', desc: '有竞争力的薪资 + 股权激励' },
      { icon: '🏥', title: '健康保障', desc: '五险一金 + 商业保险' },
      { icon: '🎓', title: '学习成长', desc: '培训基金 + 技术分享' },
      { icon: '🏠', title: '工作环境', desc: '舒适办公 + 弹性工作' },
      { icon: '🎮', title: '娱乐设施', desc: '游戏室 + 健身房' },
      { icon: '🚀', title: '职业发展', desc: '晋升通道 + 导师制度' }
    ]
  }

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    experience: '',
    education: '',
    skills: '',
    expectedSalary: '',
    availableDate: '',
    coverLetter: '',
    resume: null as File | null,
    portfolio: null as File | null
  })

  const [currentStep, setCurrentStep] = useState(1)
  const totalSteps = 3

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'resume' | 'portfolio') => {
    const file = e.target.files?.[0] || null
    setFormData(prev => ({
      ...prev,
      [field]: file
    }))
  }

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1)
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const steps = [
    { id: 1, title: '基本信息', description: '填写个人基本信息' },
    { id: 2, title: '工作经历', description: '教育背景和技能' },
    { id: 3, title: '文件上传', description: '上传简历和作品集' }
  ]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    try {
      // 这里应该调用API提交简历
      // const formDataToSend = new FormData()
      // Object.entries(formData).forEach(([key, value]) => {
      //   if (value instanceof File) {
      //     formDataToSend.append(key, value)
      //   } else {
      //     formDataToSend.append(key, value)
      //   }
      // })
      // await apiService.post(`/careers/${id}/apply`, formDataToSend)

      // 模拟提交成功
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      setMessage('简历投递成功！我们会尽快与您联系。')
      setTimeout(() => {
        navigate('/careers')
      }, 2000)
    } catch (err: any) {
      setError(err.message || '投递失败，请重试')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>投递简历 - DeepLink Limited</title>
        <meta name="description" content="投递简历到DeepLink Limited" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        {/* Header */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/careers')}
                className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                返回招聘页面
              </button>
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">投递简历</h1>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                步骤 {currentStep} / {totalSteps}
              </div>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between max-w-4xl mx-auto">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className="flex items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                      currentStep >= step.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}>
                      {currentStep > step.id ? <CheckCircle className="w-5 h-5" /> : step.id}
                    </div>
                    <div className="ml-3 hidden sm:block">
                      <div className={`text-sm font-medium ${
                        currentStep >= step.id
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {step.title}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {step.description}
                      </div>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-16 h-0.5 mx-4 ${
                      currentStep > step.id
                        ? 'bg-blue-600'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            {/* Company Info Section */}
            <div className="mb-8">
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl p-8 shadow-xl border border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                      加入 {companyInfo.name}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-6 text-lg">
                      {companyInfo.description}
                    </p>
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{companyInfo.founded}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">成立时间</div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">{companyInfo.employees}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">团队规模</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">企业文化</h3>
                      {companyInfo.culture.map((item, index) => (
                        <div key={index} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">员工福利</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {companyInfo.benefits.map((benefit, index) => (
                        <div key={index} className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-700 dark:to-gray-600 rounded-lg">
                          <div className="text-2xl mb-2">{benefit.icon}</div>
                          <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{benefit.title}</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">{benefit.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Position Selection */}
            <div className="mb-8">
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl p-8 shadow-xl border border-gray-200 dark:border-gray-700">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  选择应聘职位
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {positions.map((position) => (
                    <div
                      key={position.id}
                      onClick={() => setSelectedPosition(position.id)}
                      className={`p-6 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                        selectedPosition === position.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-semibold text-gray-900 dark:text-white">{position.title}</h4>
                        <div className="text-sm text-blue-600 dark:text-blue-400 font-medium">{position.salary}</div>
                      </div>
                      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-2" />
                          {position.location}
                        </div>
                        <div className="flex items-center">
                          <Briefcase className="w-4 h-4 mr-2" />
                          {position.department}
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-2" />
                          {position.experience}
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 line-clamp-2">
                        {position.description}
                      </p>
                    </div>
                  ))}
                </div>
                {selectedPosition && (
                  <div className="mt-6 p-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    {(() => {
                      const position = positions.find(p => p.id === selectedPosition)
                      return position ? (
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            {position.title} - 职位详情
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <h5 className="font-medium text-gray-900 dark:text-white mb-2">职位要求</h5>
                              <ul className="space-y-1">
                                {position.requirements.map((req, index) => (
                                  <li key={index} className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                    {req}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <h5 className="font-medium text-gray-900 dark:text-white mb-2">福利待遇</h5>
                              <div className="flex flex-wrap gap-2">
                                {position.benefits.map((benefit, index) => (
                                  <span
                                    key={index}
                                    className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm"
                                  >
                                    {benefit}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : null
                    })()}
                  </div>
                )}
              </div>
            </div>

            {/* Messages */}
            {message && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center"
              >
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                <span className="text-green-700 dark:text-green-400">{message}</span>
              </motion.div>
            )}

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center"
              >
                <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                <span className="text-red-700 dark:text-red-400">{error}</span>
              </motion.div>
            )}

            {/* Application Form */}
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-8 py-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-700 dark:to-gray-600">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {steps[currentStep - 1].title}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      {steps[currentStep - 1].description}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">完成进度</div>
                    <div className="w-32 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {currentStep} / {totalSteps}
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Tips Section */}
              <div className="px-8 py-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                      {currentStep === 1 && '填写提示'}
                      {currentStep === 2 && '经验分享'}
                      {currentStep === 3 && '文件要求'}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {currentStep === 1 && '请确保所有信息真实有效，这将帮助我们更好地了解您'}
                      {currentStep === 2 && '详细描述您的工作经历和技能，突出与应聘职位相关的经验'}
                      {currentStep === 3 && '简历文件请使用PDF格式，作品集可以是PDF、ZIP或RAR格式'}
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-8">
                {/* Step 1: Basic Information */}
                {currentStep === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-8"
                  >
                    {/* Quick Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">6</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">热门职位</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">50+</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">团队成员</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">95%</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">员工满意度</div>
                      </div>
                    </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      姓名 *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                            className="w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      邮箱 *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                            className="w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      电话
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                            className="w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                        placeholder="请输入您的电话"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      所在城市
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                            className="w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                        placeholder="请输入您所在的城市"
                      />
                    </div>
                  </div>
                </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        期望薪资
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          name="expectedSalary"
                          value={formData.expectedSalary}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                          placeholder="请输入期望薪资范围"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        应聘职位 *
                      </label>
                      <select
                        name="position"
                        value={selectedPosition}
                        onChange={(e) => setSelectedPosition(e.target.value)}
                        required
                        className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                      >
                        <option value="">请选择应聘职位</option>
                        {positions.map((position) => (
                          <option key={position.id} value={position.id}>
                            {position.title} - {position.department} - {position.salary}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        可入职时间
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="date"
                          name="availableDate"
                          value={formData.availableDate}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Experience and Education */}
                {currentStep === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-8"
                  >
                    {/* Skills Showcase */}
                    <div className="p-6 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">我们重视的技能</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                          { skill: '区块链开发', icon: '⛓️', color: 'blue' },
                          { skill: '产品设计', icon: '🎨', color: 'purple' },
                          { skill: '硬件工程', icon: '🔧', color: 'green' },
                          { skill: '市场运营', icon: '📈', color: 'orange' }
                        ].map((item, index) => (
                          <div key={index} className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                            <div className="text-2xl mb-2">{item.icon}</div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{item.skill}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        工作经历 *
                      </label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                        <textarea
                          name="experience"
                          value={formData.experience}
                          onChange={handleInputChange}
                          required
                          rows={4}
                          className="w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                          placeholder="请详细描述您的工作经历，包括公司名称、职位、工作内容和成就..."
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        教育背景 *
                      </label>
                      <div className="relative">
                        <GraduationCap className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                        <textarea
                          name="education"
                          value={formData.education}
                          onChange={handleInputChange}
                          required
                          rows={3}
                          className="w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                          placeholder="请填写您的教育背景，包括学校、专业、学历等..."
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        专业技能 *
                      </label>
                      <div className="relative">
                        <Award className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                        <textarea
                          name="skills"
                          value={formData.skills}
                          onChange={handleInputChange}
                          required
                          rows={3}
                          className="w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                          placeholder="请列出您的专业技能，如编程语言、工具、框架等..."
                        />
                      </div>
                    </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    求职信
                  </label>
                      <div className="relative">
                        <FileText className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <textarea
                    name="coverLetter"
                    value={formData.coverLetter}
                    onChange={handleInputChange}
                          rows={5}
                          className="w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                    placeholder="请简要介绍您的背景、技能和为什么适合这个职位..."
                  />
                </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: File Upload */}
                {currentStep === 3 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-8"
                  >
                    {/* Upload Guidelines */}
                    <div className="p-6 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">文件上传指南</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                            <FileText className="w-4 h-4 mr-2 text-blue-500" />
                            简历文件
                          </h4>
                          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                            <li>• 支持PDF、DOC、DOCX格式</li>
                            <li>• 文件大小不超过10MB</li>
                            <li>• 建议包含完整的个人信息</li>
                            <li>• 突出与应聘职位相关的经验</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                            <Award className="w-4 h-4 mr-2 text-green-500" />
                            作品集
                          </h4>
                          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                            <li>• 支持PDF、ZIP、RAR格式</li>
                            <li>• 文件大小不超过20MB</li>
                            <li>• 展示您的项目作品</li>
                            <li>• 包含项目说明和成果</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      简历文件 *
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => handleFileChange(e, 'resume')}
                        required
                        className="hidden"
                        id="resume-upload"
                      />
                      <label
                        htmlFor="resume-upload"
                            className="flex flex-col items-center justify-center w-full h-32 px-4 py-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors duration-200 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
                          >
                            <Upload className="w-8 h-8 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                              {formData.resume ? formData.resume.name : '点击上传简历'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                              PDF, DOC, DOCX (最大10MB)
                            </p>
                      </label>
                    </div>
                        {formData.resume && (
                          <div className="mt-2 flex items-center text-sm text-green-600 dark:text-green-400">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            文件已选择
                          </div>
                        )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      作品集 (可选)
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".pdf,.zip,.rar"
                        onChange={(e) => handleFileChange(e, 'portfolio')}
                        className="hidden"
                        id="portfolio-upload"
                      />
                      <label
                        htmlFor="portfolio-upload"
                            className="flex flex-col items-center justify-center w-full h-32 px-4 py-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors duration-200 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
                          >
                            <FileText className="w-8 h-8 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                              {formData.portfolio ? formData.portfolio.name : '点击上传作品集'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                              PDF, ZIP, RAR (最大20MB)
                            </p>
                      </label>
                    </div>
                        {formData.portfolio && (
                          <div className="mt-2 flex items-center text-sm text-green-600 dark:text-green-400">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            文件已选择
                          </div>
                        )}
                  </div>
                </div>

                    {/* File Preview */}
                    {(formData.resume || formData.portfolio) && (
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">已上传文件</h4>
                        <div className="space-y-2">
                          {formData.resume && (
                            <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border">
                              <div className="flex items-center">
                                <FileText className="w-4 h-4 text-blue-500 mr-2" />
                                <span className="text-sm text-gray-700 dark:text-gray-300">{formData.resume.name}</span>
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {(formData.resume.size / 1024 / 1024).toFixed(2)} MB
                              </div>
                            </div>
                          )}
                          {formData.portfolio && (
                            <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border">
                              <div className="flex items-center">
                                <FileText className="w-4 h-4 text-green-500 mr-2" />
                                <span className="text-sm text-gray-700 dark:text-gray-300">{formData.portfolio.name}</span>
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {(formData.portfolio.size / 1024 / 1024).toFixed(2)} MB
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Navigation Buttons */}
                <div className="pt-8 border-t border-gray-200 dark:border-gray-700">
                  {/* Progress Summary */}
                  <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                          <span className="text-gray-600 dark:text-gray-400">已选择职位</span>
                        </div>
                        <div className="flex items-center">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                          <span className="text-gray-600 dark:text-gray-400">基本信息</span>
                        </div>
                        {currentStep >= 2 && (
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                            <span className="text-gray-600 dark:text-gray-400">工作经历</span>
                          </div>
                        )}
                        {currentStep >= 3 && (
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                            <span className="text-gray-600 dark:text-gray-400">文件上传</span>
                          </div>
                        )}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">
                        {selectedPosition && positions.find(p => p.id === selectedPosition)?.title}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex space-x-4">
                      {currentStep > 1 && (
                        <button
                          type="button"
                          onClick={prevStep}
                          className="flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <ArrowLeft className="w-4 h-4 mr-2" />
                          上一步
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => navigate('/careers')}
                        className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        取消
                      </button>
                    </div>

                    <div className="flex space-x-4">
                      {currentStep < totalSteps ? (
                        <button
                          type="button"
                          onClick={nextStep}
                          className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          下一步
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </button>
                      ) : (
                        <button
                          type="submit"
                          disabled={loading}
                          className="flex items-center px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                        >
                          {loading ? (
                            <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          {loading ? '投递中...' : '投递简历'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Additional Content Sections */}
            <div className="mt-12 space-y-8">
              {/* Interview Process */}
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl p-8 shadow-xl border border-gray-200 dark:border-gray-700">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  面试流程
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {[
                    { step: '01', title: '简历筛选', desc: 'HR初步筛选简历', duration: '1-2天' },
                    { step: '02', title: '电话面试', desc: '了解基本情况和动机', duration: '30分钟' },
                    { step: '03', title: '技术面试', desc: '技术能力和项目经验', duration: '1-2小时' },
                    { step: '04', title: '终面', desc: '团队匹配和最终确认', duration: '1小时' }
                  ].map((item, index) => (
                    <div key={index} className="text-center">
                      <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-white font-bold text-lg">{item.step}</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{item.desc}</p>
                      <p className="text-xs text-blue-600 dark:text-blue-400">{item.duration}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Success Stories */}
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl p-8 shadow-xl border border-gray-200 dark:border-gray-700">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  成功案例
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                    {
                      name: '张工程师',
                      position: '区块链开发工程师',
                      quote: '在DeepLink工作让我接触到了最前沿的Web3技术，团队氛围很好，成长很快。',
                      achievement: '主导开发了核心DeFi协议，用户量增长300%'
                    },
                    {
                      name: '李设计师',
                      position: 'UI/UX设计师',
                      quote: '这里的设计文化很开放，鼓励创新，我的设计能力得到了很大提升。',
                      achievement: '设计的界面获得了行业设计大奖'
                    },
                    {
                      name: '王产品经理',
                      position: '产品经理',
                      quote: '从0到1打造产品很有成就感，团队协作效率很高。',
                      achievement: '负责的产品月活用户突破100万'
                    }
                  ].map((story, index) => (
                    <div key={index} className="p-6 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 rounded-lg">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                          {story.name.charAt(0)}
                        </div>
                        <div className="ml-3">
                          <div className="font-semibold text-gray-900 dark:text-white">{story.name}</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">{story.position}</div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 italic">"{story.quote}"</p>
                      <div className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                        {story.achievement}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQ Section */}
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl p-8 shadow-xl border border-gray-200 dark:border-gray-700">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  常见问题
                </h3>
                <div className="space-y-4">
                  {[
                    {
                      question: '面试需要准备什么？',
                      answer: '请准备个人简历、作品集（如有）、技术项目经验介绍，以及对我们公司和Web3行业的了解。'
                    },
                    {
                      question: '工作地点可以灵活安排吗？',
                      answer: '我们支持远程工作，部分职位可以完全远程，具体根据职位要求而定。'
                    },
                    {
                      question: '公司提供培训机会吗？',
                      answer: '是的，我们提供技术培训、行业会议参与、在线课程学习等多种成长机会。'
                    },
                    {
                      question: '薪资待遇如何？',
                      answer: '我们提供有竞争力的薪资，包括基本工资、绩效奖金、股权激励等多元化薪酬体系。'
                    }
                  ].map((faq, index) => (
                    <div key={index} className="border-b border-gray-200 dark:border-gray-600 pb-4">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{faq.question}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
                <div className="text-center">
                  <h3 className="text-2xl font-bold mb-4">还有其他问题？</h3>
                  <p className="text-blue-100 mb-6">我们的人力资源团队随时为您解答疑问</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="flex items-center justify-center">
                      <Mail className="w-5 h-5 mr-2" />
                      <span>hr@deeplink.com</span>
                    </div>
                    <div className="flex items-center justify-center">
                      <Phone className="w-5 h-5 mr-2" />
                      <span>+86 400-888-8888</span>
                    </div>
                    <div className="flex items-center justify-center">
                      <MapPin className="w-5 h-5 mr-2" />
                      <span>北京市朝阳区科技园区</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default JobApplication